import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { InstaLearnClientState } from '@app/model/insta-learn-client-state';
import { SlideDTO } from '@app/model/slideDTO.model';
import { NavigateService } from '@app/services/navigate.service';
import { PayableNodeNavigationService } from '@app/services/payable-node-navigation.service';
import { InstaLearnPostsDialogService, InstaLearnTopic, NodeModel, InstaLearnApiService, InstaLearn } from 'library-explorer';
import { catchError, filter, map, Observable } from 'rxjs';

@Component({
    selector: 'app-slide-insta-learn-topics',
    templateUrl: './slide-insta-learn-topics.component.html',
    styleUrls: ['./slide-insta-learn-topics.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SlideInstaLearnTopicsComponent implements OnInit {
  @Input() public data: SlideDTO;

  public items: InstaLearnTopic[] = [];

  constructor(
    private readonly navigateService: NavigateService,
    private readonly payableNodeNavigationService: PayableNodeNavigationService,
    private readonly instaLearnPostsDialogService: InstaLearnPostsDialogService,
    private readonly instaLearnApiService: InstaLearnApiService) { }

  ngOnInit(): void {
    this.loadTopics();
  }

  public openDialog(topic: InstaLearnTopic): void {
    const index = this.items.indexOf(topic);
    const dialog = this.instaLearnPostsDialogService.openTopicsDialog({
      topics: this.items,
      cardsSourceFn: topic => this.getInstaLearnsForTopic(topic),
      index
    });

    dialog.componentInstance.cardViewed.subscribe(({ topic, card }) => {
      this.markTopicCardAsViewed(topic, card);
    });

    dialog.afterClosed()
      .pipe(
        filter(instaLearn => !!instaLearn)
      )
      .subscribe(instaLearn => {
        this.navigate(instaLearn);
      });
  }

  public navigate(item: InstaLearn): void {
    if (!item) {
      return;
    }

    if (item.link?.uri) {
      this.navigateService.navigate(item.link.uri);
      return;
    }

    this.payableNodeNavigationService.navigate(item.linkToContent);
  }

  private getInstaLearnsForTopic(topic: InstaLearnTopic): Observable<InstaLearnClientState[]> {
    return this.instaLearnApiService.getInstaLearns({ sort: 'created:DESC', showOnlyUnread: true, topic: topic.id })
      .pipe(
        map(data => data.items),
        map(items => items.map(item => ({ ...item, viewed: topic.unreadCount === 0 })))
      )
  }

  private loadTopics(): void {
    this.instaLearnApiService.getInstaLearnTopics({ sort: 'unread:DESC,weight:ASC', hideEmpty: true })
      .subscribe(data => {
        this.items = data.items;
      })
  }

  private markTopicCardAsViewed(topic: InstaLearnTopic, card: InstaLearnClientState): void {
    if (!card || card.viewed) {
      return;
    }

    card.viewed = true;
    topic.unreadCount = Math.max(topic.unreadCount - 1, 0);

    this.instaLearnApiService.markInstaLearnAsViewed(card.id)
      .pipe(
        catchError(err => {
          card.viewed = false;
          topic.unreadCount++;
          throw err;
        })
      )
      .subscribe();
  }
}
